import './defines';
import { ChannelModel } from '@sosocio/creator-types/app';
import ButtonComponent from 'components/button';
import EventBus from 'components/event-bus';
import GoogleButtonComponent from 'components/google-button';
import analytics from 'controllers/analytics';
import auth from 'controllers/auth';
import connector from 'controllers/connector';
import URL_PARAMETERS from 'settings/url-parameters';
import {
	ChannelsModule,
	ConfigModule,
	UserModule,
} from 'store';
import parseUrl from 'tools/parse-url';
import {
	Component,
	toNative,
} from 'utils/vue-facing-decorator';
import {
	Prop,
	Vue,
} from 'vue-facing-decorator';
import Template from './template.vue';

@Component({
	components: {
		ButtonComponent,
		GoogleButtonComponent,
	},
	mixins: [Template],
})
class LoginView extends Vue {
	@Prop({
		default: false,
		type: Boolean,
	})
	readonly showGuest!: boolean;

	@Prop({
		default: false,
		type: Boolean,
	})
	readonly showGoogleLogin!: boolean;

	@Prop({
		default: null,
		type: String,
	})
	public readonly username!: string | null;

	private get connections() {
		const connections: ChannelModel['id'][] = [];

		if (this.showGoogleLogin) {
			connections.push('gplus');
		}

		return connections;
	}

	protected get formTarget() {
		return `${BASE_URL || window.glAppUrl}/api/auth/login`;
	}

	protected get hasThirdPartyLogin() {
		return this.showGoogleLogin;
	}

	protected get labelName() {
		return ConfigModule.labelTagName;
	}

	private inputData: {
		username: string | null;
		password: string | null;
	} = {
		username: null,
		password: null,
	};

	protected invalidPassword = false;

	protected invalidUserName = false;

	private showPassword = false;

	protected created() {
		const parsedUrl = parseUrl(window.location.href);

		if (URL_PARAMETERS.email in parsedUrl.parameters) {
			this.inputData.username = parsedUrl.parameters[URL_PARAMETERS.email];
		}
	}

	protected mounted() {
		if (this.username) {
			this.inputData.username = this.username;
		}

		this.connections.forEach((channelId) => {
			connector
				.setup(channelId)
				.then(() => connector.init(channelId))
				.catch(() => {
					// Swallow error: no action required
				});
		});
	}

	protected back() {
		auth.open();
	}

	protected cancel() {
		auth.cancel();
	}

	protected checkKey(e: KeyboardEvent) {
		if (e.keyCode == 13) {
			this.checkLogin();
		}
	}

	private checkLogin() {
		analytics.trackEvent(
			'Submit login',
			{},
		);

		this.invalidUserName = false;
		this.invalidPassword = false;

		const {
			username,
			password,
		} = this.inputData;

		if (
			!password
			|| password.length === 0
		) {
			analytics.trackEvent(
				'Login failed',
				{
					reason: 'No password provided',
				},
			);

			this.invalidPassword = true;
		} else if (
			!username
			|| username.length === 0
			|| username.indexOf('@') === -1
		) {
			this.invalidUserName = true;

			analytics.trackEvent(
				'Login failed',
				{
					reason: 'Invalid user name',
				},
			);
		} else {
			// Show loader
			const closeLoader = this.$openLoaderDialog();

			// close login dialog
			auth.closeDialog();

			auth
				.login({
					username,
					password,
				})
				.then(
					() => {
						if (UserModule.id) {
							analytics.trackEvent(
								'Login',
								{},
							);

							EventBus.emit(
								'auth:login',
								true,
							);
						} else {
							analytics.trackEvent(
								'Login failed',
								{
									reason: 'No user id returned',
								},
							);

							auth.showLogin({
								username,
							});
						}
					},
					(err) => {
						const errorMessage = err instanceof Error
							? err.message
							: err;

						auth.showLogin({
							username,
						});

						this.$openErrorDialog({
							body: {
								content: errorMessage,
							},
						});

						analytics.trackEvent(
							'Login failed',
							{
								reason: errorMessage,
							},
						);
					},
				)
				.finally(() => {
					// Close loader
					closeLoader();
				});
		}
	}

	protected continueAsGuest() {
		analytics.trackEvent(
			'Login as guest',
			{
				category: 'Button',
			},
		);

		auth.guest();
	}

	protected async forgotPassword() {
		const { username } = this.inputData;

		analytics.trackEvent(
			'Forgot password',
			{},
		);

		const validEmail = username
			&& username.length > 0
			&& username.indexOf('@') >= 0;

		if (!validEmail) {
			this.invalidUserName = true;
			return;
		}

		// Show loader
		const closeLoader = this.$openLoaderDialog();

		try {
			await auth.forgotPassword(username);

			// Show message saying password reset email has been send
			const closeAlert = this.$openAlertDialog({
				header: {
					title: this.$t('dialogHeaderForgotPassword'),
				},
				body: {
					content: this.$t('dialogTextForgotPassword'),
				},
				footer: {
					buttons: [
						{
							id: 'accept',
							text: this.$t('dialogButtonForgotPasswordOk'),
							click: () => {
								auth.showLogin({
									username,
								});
								closeAlert();
							},
						},
					],
				},
			});
		} catch (error: any) {
			const closeError = this.$openErrorDialog({
				body: {
					content: error.message,
				},
				footer: {
					buttons: [
						{
							id: 'accept',
							text: this.$t('dialogButtonOk'),
							click: () => {
								auth.showLogin({
									username,
								});
								closeError();
							},
						},
					],
				},
			});
		}

		// Close loader
		closeLoader();
	}

	protected async googleLogin() {
		const networkModel = ChannelsModule.getById('gplus');

		if (!networkModel) {
			throw new Error('Could not find required network model');
		}

		analytics.trackEvent(
			'Login with Google',
			{
				category: 'Button',
			},
		);

		// Close login dialog
		auth.closeDialog();

		// Open loader dialog
		const closeLoader = this.$openLoaderDialog();

		try {
			const bearerToken = await connector.login(
				'gplus',
				{
					display: 'page',
					scopeCategories: [
						'basic',
						'login',
					],
				},
			);

			await auth.login({
				googleAccessToken: bearerToken || undefined,
			});

			if (UserModule.id) {
				analytics.trackEvent(
					'Login',
					{},
				);

				EventBus.emit(
					'auth:login',
					true,
				);
			} else {
				analytics.trackEvent(
					'Login failed',
					{
						reason: 'No user id returned',
					},
				);

				auth.showLogin();
			}
		} catch (err: any) {
			const errorMessage = err instanceof Error
				? err.message
				: err;

			auth.showLogin();

			this.$openErrorDialog({
				body: {
					content: errorMessage,
				},
			});

			analytics.trackEvent(
				'Login failed',
				{
					reason: errorMessage,
				},
			);
		}

		// Close loader
		closeLoader();
	}

	protected signup() {
		auth.showSignup();
	}

	protected togglePasswordVisiblity() {
		this.showPassword = !this.showPassword;
	}
}

export default toNative(LoginView);
